import {
  combineLatest,
  distinctUntilChanged,
  map,
  type Observable,
  of,
  switchMap,
} from 'rxjs';

import type { DatabaseInfo } from './database-discovery';

export type DatabaseCatalogNode = {
  fields: Record<string, string | string[] | undefined>;
};

const databaseCatalogCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

function compareCatalogText(left: string, right: string): number {
  return (
    databaseCatalogCollator.compare(left, right) ||
    (left < right ? -1 : left > right ? 1 : 0)
  );
}

/**
 * Discovery sources complete independently. Keep their arrival order out of
 * every database picker by applying one deterministic workspace-wide order.
 */
export function sortDatabaseCatalog(
  databases: readonly DatabaseInfo[]
): DatabaseInfo[] {
  return [...databases].sort(
    (left, right) =>
      compareCatalogText(left.docTitle, right.docTitle) ||
      compareCatalogText(left.docId, right.docId) ||
      compareCatalogText(left.databaseTitle, right.databaseTitle) ||
      compareCatalogText(left.blockId, right.blockId)
  );
}

function getDatabaseCatalogFingerprint(database: DatabaseInfo): string {
  return JSON.stringify([
    database.docId,
    database.blockId,
    database.docTitle,
    database.databaseTitle,
    database.xywh,
    database.isInTrash === true,
    database.schemaLoaded,
    database.visible,
    database.databaseRootDocId,
    database.databaseDirectoryPartDocIds,
    database.databaseRowShardDocIds,
    database.schemaLocked,
    database.systemManaged,
    database.managedBy,
    database.streamlainRole,
    database.tableRole,
    database.columns,
  ]);
}

/** Semantic equality after canonical sorting; ignores source emission timing. */
export function areDatabaseCatalogsEqual(
  left: readonly DatabaseInfo[],
  right: readonly DatabaseInfo[]
): boolean {
  return (
    left.length === right.length &&
    left.every(
      (database, index) =>
        getDatabaseCatalogFingerprint(database) ===
        getDatabaseCatalogFingerprint(right[index])
    )
  );
}

/** The existing workspace index supplies metadata; this never opens documents. */
export function mergeDatabaseCatalog(
  nodes: readonly DatabaseCatalogNode[],
  loaded: readonly DatabaseInfo[],
  loadedDocIds: readonly string[],
  documents: ReadonlyMap<string, { title: string; trashed: boolean }>
): DatabaseInfo[] {
  const authoritativeDocs = new Set(loadedDocIds);
  const byId = new Map<string, DatabaseInfo>();
  const first = (value: string | string[] | undefined) =>
    Array.isArray(value) ? value[0] : value;
  for (const { fields } of nodes) {
    const docId = first(fields.docId);
    const blockId = first(fields.blockId);
    if (!docId || !blockId || authoritativeDocs.has(docId)) continue;
    const document = documents.get(docId);
    if (!document || document.trashed) continue;
    let metadata: { databaseName?: unknown } = {};
    try {
      metadata = JSON.parse(first(fields.additional) ?? '{}');
    } catch {
      // Old index entries without structured metadata still have their identity.
    }
    const title =
      typeof metadata?.databaseName === 'string'
        ? metadata.databaseName.trim()
        : '';
    byId.set(blockId, {
      blockId,
      docId,
      docTitle: document.title,
      databaseTitle: title || 'Untitled',
      columns: [],
      schemaLoaded: false,
    });
  }
  for (const database of loaded) byId.set(database.blockId, database);
  return sortDatabaseCatalog([...byId.values()]);
}

/** Subscribe to the canonical guard; do not retain a second, stale rights cache. */
export function watchPermittedDatabases(
  databases$: Observable<DatabaseInfo[]>,
  canRead: (docId: string) => Observable<boolean | undefined>,
  canWrite?: (docId: string) => Observable<boolean | undefined>
): Observable<DatabaseInfo[]> {
  return databases$.pipe(
    switchMap(databases => {
      const docIds = [...new Set(databases.map(db => db.docId))];
      if (!docIds.length) return of([]);
      return combineLatest(
        docIds.map(docId =>
          canWrite
            ? combineLatest([canRead(docId), canWrite(docId)]).pipe(
                map(([read, write]) => read === true && write === true)
              )
            : canRead(docId).pipe(map(read => read === true))
        )
      ).pipe(
        map(allowed => {
          const permitted = new Set(
            docIds.filter((_, index) => allowed[index])
          );
          return sortDatabaseCatalog(
            databases.filter(database => permitted.has(database.docId))
          );
        })
      );
    }),
    distinctUntilChanged<DatabaseInfo[]>(areDatabaseCatalogsEqual)
  );
}
